import { Button, TextField, Alert, Box, Card, CardContent, Typography, Container, CircularProgress, Divider, IconButton, InputAdornment, Dialog, DialogContent, DialogTitle, Fade, Link as MuiLink, CssBaseline } from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityIconOff,
  Login as LoginIcon,
  Support as SupportIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SupabaseUserContext } from "../contexts/supabaseUser.context";

import InputValidator from "../utils/security/inputValidator";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import AuthPageBottomAnimation from "../components/AuthPageBottomAnimation";
import AddToHomeScreenPopup from "../components/AddToHomeScreenPopup";
import {
  authFieldSx,
  authPageOuterSx,
  authPageContainerSx,
  authCardShellSx,
  authAlertErrorSx,
  authAlertWarningSx,
  authAlertInfoSx,
  authLogoSx,
} from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import { BRAND_PRIMARY } from "../constants/brandColors";
import { sanitizeSameOriginRedirect } from "../utils/security/safeRedirect";
import { AUTH_FAILED_FLAG } from "../utils/security/tokenManager";
import { getMm } from "../theme/modernMaterialEnterprise";

const LoginContent = () => {
  const { user, fetchUser, emailPasswordLogin, logOutUser } = useContext(SupabaseUserContext);
  const { isDarkMode } = useTheme();
  const { primaryButtonSx, outlineButtonSx, dialogFormPrimarySx } = useSystemButtonStyles();
  const { t } = useTranslation();
  const fieldSx = useMemo(() => authFieldSx(isDarkMode), [isDarkMode]);
  const logoSx = useMemo(() => authLogoSx(isDarkMode), [isDarkMode]);

  const [form, setForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const onFormInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const redirectNow = () => {
    const redirectTo = sanitizeSameOriginRedirect(
      new URLSearchParams(location.search).get("redirectTo")
    );
    navigate(redirectTo);
  };

  const loadUser = async () => {
    try {
      if (sessionStorage.getItem(AUTH_FAILED_FLAG) === "1") {
        sessionStorage.removeItem(AUTH_FAILED_FLAG);
        await logOutUser();
        return;
      }
    } catch {
      /* storage unavailable */
    }
    if (!user) {
      const fetchedUser = await fetchUser();
      if (fetchedUser) {
        redirectNow();
      }
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const reason = new URLSearchParams(location.search).get("reason");
    if (reason === "platform_suspended") {
      setLoginError(t("login_platform_suspended"));
    }
  }, [location.search, t]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type");
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hashType = hashParams.get("type");

    if (type === "signup" || type === "email" || hashType === "signup" || hashType === "email") {
      setShowConfirmationPopup(true);
      if (type || hashType) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [location]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoginError("");
    setIsLoading(true);

    const emailValidation = InputValidator.validateEmail(form.email);
    const passwordValidation = InputValidator.validatePassword(form.password);

    if (!emailValidation.isValid) {
      setLoginError(emailValidation.error);
      setIsLoading(false);
      return;
    }

    if (!passwordValidation.isValid) {
      setLoginError(passwordValidation.error);
      setIsLoading(false);
      return;
    }

    try {
      await emailPasswordLogin(emailValidation.value, passwordValidation.value);
      redirectNow();
    } catch (error) {
      setLoginError(error.message || t("login_invalid_email_password"));
    } finally {
      setIsLoading(false);
    }
  };

  const cardShellSx = authCardShellSx(isDarkMode);

  return (
    <>
      <CssBaseline />
      <Box sx={{ ...authPageOuterSx(isDarkMode), position: "relative", overflow: "hidden" }}>
        <AuthPageBottomAnimation isDarkMode={isDarkMode} />
        <AuthPageToggleBar />
        <Container maxWidth="md" sx={authPageContainerSx}>
          <Fade in timeout={800}>
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 440 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box sx={{ textAlign: "center", mb: 4, mt: { xs: 1, sm: 0 }, width: "100%" }}>
                <Box
                  component="img"
                  src={isDarkMode ? "NewEraLogo.png" : "NewEraBlackLogo.png"}
                  alt="NewEra Logo"
                  sx={logoSx}
                />
              </Box>

              <Card sx={{ ...cardShellSx, width: "100%" }}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "1.875rem",
                        fontWeight: 700,
                        color: getMm(isDarkMode).onSurface,
                        mb: 1,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("login_welcome_back")}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: getMm(isDarkMode).onSurfaceVariant,
                        fontWeight: 500,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("login_sign_in_to_continue")}
                    </Typography>
                  </Box>

                  {loginError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 3, ...authAlertErrorSx(isDarkMode) }}
                    >
                      {loginError}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={onSubmit} sx={{ mb: 2 }}>
                    <TextField
                      label={t("login_email_address")}
                      type="email"
                      variant="outlined"
                      name="email"
                      value={form.email}
                      onChange={onFormInputChange}
                      required
                      fullWidth
                      autoComplete="email"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...fieldSx, mb: 2 }}
                    />

                    <TextField
                      label={t("login_password")}
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      name="password"
                      value={form.password}
                      onChange={onFormInputChange}
                      required
                      fullWidth
                      autoComplete="current-password"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((v) => !v)}
                              edge="end"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              sx={{
                                color: getMm(isDarkMode).onSurfaceVariant,
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              {showPassword ? <VisibilityIconOff /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...fieldSx, mb: 2.5 }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isLoading}
                      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                      sx={{ ...primaryButtonSx, py: 1.5, fontSize: "0.875rem", textTransform: "none" }}
                    >
                      {isLoading ? t("login_signing_in") : t("login_sign_in")}
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 2.5, borderColor: getMm(isDarkMode).outlineVariant }}>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: getMm(isDarkMode).onSurfaceVariant,
                        px: 2,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("login_need_help")}
                    </Typography>
                  </Divider>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Button
                      component={RouterLink}
                      to="/signup"
                      variant="outlined"
                      fullWidth
                      startIcon={<PersonAddIcon />}
                      sx={{ ...outlineButtonSx, textTransform: "none", justifyContent: "flex-start" }}
                    >
                      {t("login_no_account")}
                    </Button>
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        color: getMm(isDarkMode).onSurfaceVariant,
                        lineHeight: 1.5,
                        px: 0.5,
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      {t("login_already_registered_hint")}
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/reset-password"
                      variant="outlined"
                      fullWidth
                      startIcon={<LockResetIcon />}
                      sx={{ ...outlineButtonSx, textTransform: "none", justifyContent: "flex-start" }}
                    >
                      {t("login_forgot_password")}
                    </Button>
                    <Button
                      component="a"
                      href="mailto:support@neweracss.com"
                      variant="outlined"
                      fullWidth
                      startIcon={<SupportIcon />}
                      sx={{ ...outlineButtonSx, textTransform: "none", justifyContent: "flex-start" }}
                    >
                      {t("login_need_support")}
                    </Button>
                    <Typography
                      sx={{
                        textAlign: "center",
                        mt: 1.5,
                        fontSize: "0.8125rem",
                        fontFamily: APP_FONT_FAMILY,
                      }}
                    >
                      <MuiLink
                        component={RouterLink}
                        to="/privacy"
                        sx={{
                          color: getMm(isDarkMode).onSurfaceVariant,
                          textDecoration: "none",
                          "&:hover": { color: getMm(isDarkMode).onSurfaceVariant },
                        }}
                      >
                        {t("login_privacy_policy")}
                      </MuiLink>
                      {" · "}
                      <MuiLink
                        component={RouterLink}
                        to="/data-deletion"
                        sx={{
                          color: getMm(isDarkMode).onSurfaceVariant,
                          textDecoration: "none",
                          "&:hover": { color: getMm(isDarkMode).onSurfaceVariant },
                        }}
                      >
                        {t("login_data_deletion")}
                      </MuiLink>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Dialog
        open={showConfirmationPopup}
        onClose={() => setShowConfirmationPopup(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "none",
            backgroundColor: getMm(isDarkMode).surfaceLowest,
            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
            fontFamily: APP_FONT_FAMILY,
          },
        }}
      >
        <DialogTitle sx={{ position: "relative", textAlign: "center", pb: 2 }}>
          <IconButton
            onClick={() => setShowConfirmationPopup(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: getMm(isDarkMode).onSurface,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 1 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: BRAND_PRIMARY.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: getMm(isDarkMode).onSurface,
                fontFamily: APP_FONT_FAMILY,
              }}
            >
              {t("login_email_confirmed_title")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", px: 1, pb: 2 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: getMm(isDarkMode).onSurfaceVariant,
                mb: 3,
                lineHeight: 1.6,
                fontFamily: APP_FONT_FAMILY,
              }}
            >
              {t("login_email_confirmed_message")}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setShowConfirmationPopup(false)}
              sx={{ ...dialogFormPrimarySx, py: 1.5, textTransform: "none" }}
            >
              {t("login_continue_sign_in")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <AddToHomeScreenPopup />
    </>
  );
};

const Login = () => (
  <CustomThemeProvider>
    <LoginContent />
  </CustomThemeProvider>
);

export default Login;
